import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { evaluadorService } from '../services/evaluadorService';
import { EvaluadorCrearInput, EvaluadorInput, EvaluadorScope } from '../types/evaluador';

export function useEvaluadores(q: string, scope?: EvaluadorScope, ocultos = false) {
  return useQuery({
    queryKey: ['evaluadores', q, scope ?? null, ocultos],
    queryFn: () => evaluadorService.list(q, scope, ocultos),
    placeholderData: keepPreviousData,
  });
}

/**
 * Carga por docente (el gráfico de Evaluadores).
 *
 * Sin `scope` es la carga que arrastra hoy, de cualquier semestre, y solo lo abierto.
 * Con un período es lo que le tocó ese semestre, cerrado o no.
 */
export function useCargaEvaluadores(scope?: EvaluadorScope) {
  return useQuery({
    queryKey: ['evaluadores-carga', scope ?? null],
    queryFn: () => evaluadorService.carga(scope),
    placeholderData: keepPreviousData,
  });
}

/** Todo lo que cambia al tocar un docente: el catálogo, su ficha y la carga del gráfico. */
function refrescarEvaluadores(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['evaluadores'] });
  qc.invalidateQueries({ queryKey: ['evaluador-resumen'] });
  qc.invalidateQueries({ queryKey: ['evaluadores-carga'] });
}

export function usePeriodos() {
  return useQuery({ queryKey: ['periodos'], queryFn: evaluadorService.periodos });
}

export function useEvaluadorResumen(rut: number) {
  return useQuery({
    queryKey: ['evaluador-resumen', rut],
    queryFn: () => evaluadorService.resumen(rut),
    enabled: Number.isFinite(rut),
  });
}

export function useUpdateEvaluador() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rut, input }: { rut: number; input: EvaluadorInput }) => evaluadorService.update(rut, input),
    onSuccess: () => refrescarEvaluadores(qc),
  });
}

/** Oculta a un docente (soft delete). Se devuelve con `useUpdateEvaluador({ vigente: true })`. */
export function useOcultarEvaluador() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rut: number) => evaluadorService.ocultar(rut),
    onSuccess: () => refrescarEvaluadores(qc),
  });
}

export function useCrearEvaluador() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EvaluadorCrearInput) => evaluadorService.create(input),
    onSuccess: () => refrescarEvaluadores(qc),
  });
}

export function useSincronizarEvaluadores() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commit: boolean) => evaluadorService.sincronizar(commit),
    onSuccess: (data) => {
      if (data.publicado) refrescarEvaluadores(qc);
    },
  });
}

/** Correo del coordinador a los docentes seleccionados. No invalida nada: no cambia datos. */
export function useNotificarEvaluadores() {
  return useMutation({ mutationFn: evaluadorService.notificar });
}
